import React, { useEffect } from 'react';
import Logo from 'components/logo';
import { formatPrice } from 'utils/utils';
import { formatDateToDayMonthYear } from 'utils/dateHelpers';
import useBankAccounts from 'hooks/v1/pages/invoices/useBankAccounts';
import { DISCOUNT_TYPES } from 'utils/constants';
import clsx from 'clsx';

const invoiceData = {
    invoiceNumber: 'INV-20240906019',
    issueDate: '15 September 2024',
    customerDetails: {
        name: 'Komang Anggi Waisnawati',
        address: 'Jl. Raya Sluat Keramas',
        email: 'yulianakomanggede@gmail.com',
        phone: '6289606561716',
    },
    items: [
        {
            description: 'Wifi 30Mbps Internet',
            qty: 1,
            unitPrice: 365000,
            amount: 365000
        }
    ],
    totals: {
        subtotal: 365000,
        tax: 0,
        grandTotal: 365000
    }
};

const InvoicePreview = ({ invoice, className }) => {
    const { invoiceNumber } = invoiceData;

    const { bankAccounts, fetchBankAccounts } = useBankAccounts();

    // Fungsi untuk menghitung subtotal
    const calculateSubtotal = () => {
        if (!invoice.form.items || invoice.form.items.length === 0) return 0;

        return invoice.form.items.reduce((acc, item) => acc + (item.price * item.qty), 0);
    };

    // Fungsi untuk menghitung diskon berdasarkan type (percent atau fixed)
    const calculateDiscount = (subtotal) => {
        if (!invoice.form.discount) return 0;

        if (invoice.form.discount_type === DISCOUNT_TYPES[1].value) {
            return (subtotal * invoice.form.discount) / 100;
        } else if (invoice.form.discount_type === DISCOUNT_TYPES[0].value) {
            return invoice.form.discount;
        }
        return 0;
    };

    // Fungsi untuk menghitung pajak berdasarkan persentase
    const calculateTax = (amount, taxPercentage) => {
        if (!taxPercentage) return 0;
        return (amount * taxPercentage) / 100;
    };

    // Fungsi untuk menghitung grand total setelah diskon dan pajak
    const calculateGrandTotal = (subtotal, discount, taxPercentage) => {
        const afterDiscount = subtotal - discount;
        const tax = calculateTax(afterDiscount, taxPercentage);
        return afterDiscount + tax;
    };

    const subtotal = calculateSubtotal();
    const discount = calculateDiscount(subtotal);
    const taxPercentage = invoice.form?.tax || 0; // Mengambil nilai pajak dari invoice.form
    const grandTotal = calculateGrandTotal(subtotal, discount, taxPercentage);
    const taxAmount = calculateTax(subtotal - discount, taxPercentage);

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    return (
        <div className={clsx("card shadow-sm rounded-3", className)}>
            <div className="card-body d-flex flex-column h-100">
                {/* Header */}
                <div className="row mb-4">
                    <Logo className="navbar-brand-image mb-4" style={{ height: '5rem' }} />
                    <div className="col-6">
                        <p className="mb-1 fw-bold">Gotrasoft IT Solutions</p>
                        <p className="mb-1">mang@gotrasoft.com</p>
                        <p className="mb-1">+293871283</p>
                    </div>
                    <div className="col-6 text-end">
                        <p>
                            <strong>{invoice.form.customer ? invoice.form.customer?.name : "Name"}</strong>
                            <br />
                            {invoice.form.customer ? invoice.form.customer?.address : "Address"}
                            <br />
                            {invoice.form.customer ? invoice.form.customer?.email : "Email"}
                            <br />
                            {invoice.form.customer ? invoice.form.customer?.whatsapp : "Phone"}
                        </p>
                    </div>
                </div>

                {/* Invoice Info */}
                <div className="d-flex col-12 justify-content-between">
                    <h5 className="card-title">
                        INVOICE {invoice?.form?.invoice_number || 'DUMMY-1234567890'}
                    </h5>
                    <p>
                        <strong>Due Date:</strong>{" "}
                        {invoice.form?.due_date ? formatDateToDayMonthYear(invoice.form?.due_date) : ""}
                    </p>
                </div>

                {/* Table */}
                <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                        <thead className="table-light">
                            <tr>
                                <th>No</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Quantity</th>
                                <th>Price</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!invoice.form.items?.length ? (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No items
                                    </td>
                                </tr>
                            ) : (
                                invoice.form.items?.map((item, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{item.name}</td>
                                        <td>{item.description}</td>
                                        <td>{item.qty}</td>
                                        <td>{formatPrice(item.price)}</td>
                                        <td>{formatPrice(item.qty * item.price)}</td>
                                    </tr>
                                ))
                            )}
                            <tr>
                                <td colSpan="5" className="text-end">
                                    Subtotal
                                </td>
                                <td>{formatPrice(subtotal)}</td>
                            </tr>
                            {invoice.form.discount && (
                                <tr>
                                    <td colSpan="5" className="text-end">
                                        Discount (
                                        {invoice.form.discount_type === DISCOUNT_TYPES[0].value
                                            ? `- ${formatPrice(invoice.form.discount)}`
                                            : `${invoice.form.discount}%`}
                                        )
                                    </td>
                                    <td>Rp. {(subtotal - discount).toLocaleString()}</td>
                                </tr>
                            )}
                            <tr>
                                <td colSpan="5" className="text-end">
                                    Tax ({taxPercentage}%)
                                </td>
                                <td>{formatPrice(taxAmount)}</td>
                            </tr>
                            <tr>
                                <td colSpan="5" className="text-end">
                                    <strong>Grand Total</strong>
                                </td>
                                <td>
                                    <strong>{formatPrice(grandTotal)}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Table Transactions */}
                {invoice.form.transactions && (
                    <>
                        <h3 className="card-title mt-4">Transactions</h3>
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                                <thead className="table-light">
                                    <tr>
                                        <th>No</th>
                                        <th>Transaction Date</th>
                                        <th>Payment Method</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!invoice.form.transactions?.length ? (
                                        <tr>
                                            <td colSpan="4" className="text-center">
                                                No transactions
                                            </td>
                                        </tr>
                                    ) : (
                                        invoice.form.transactions?.map((transaction, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{formatDateToDayMonthYear(transaction.date)}</td>
                                                <td>{transaction.payment_method}</td>
                                                <td>{formatPrice(transaction.amount)}</td>
                                            </tr>
                                        ))
                                    )}
                                    {/* Total Transactions & balance */}
                                    <tr>
                                        <td colSpan="3" className="text-end">
                                            Total Transactions
                                        </td>
                                        <td>{formatPrice(invoice.form.total_paid)}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan="3" className="text-end">
                                            MUST PAID
                                        </td>
                                        <td>{formatPrice(invoice.form.balance)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* Footer */}
                <p className="text-muted mt-3">{invoice.form?.footer || ""}</p>

                {/* Manual Transfer and Payment */}
                <div className="mt-auto d-flex justify-content-between">
                    <div className="d-flex flex-column">
                        <h5 className="card-title mt-4">Manual Transfer</h5>
                        {bankAccounts.loading ? (
                            <p>Loading...</p>
                        ) : bankAccounts.data?.length > 0 ? (
                            bankAccounts.data.map((data, index) => (
                                <div key={index} className="mb-4">
                                    <p>
                                        {data.bank_name} - {data.bank_number}
                                        <br />
                                        a/n {data.bank_account}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p>No bank account</p>
                        )}
                    </div>
                    <div>
                        <h5 className="card-title mt-4">Payment</h5>
                        {invoice.form.balance === 0 ? (
                            <h3 className="text-success">PAID</h3>
                        ) : (
                            <h3 className="text-danger">UNPAID</h3>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default InvoicePreview;

const TransactionButton = ({ invoice }) => {
    return (
        <button className="btn btn-primary mt-3">Pay Now</button>
    );
};